import React, { useEffect, useState, useRef } from 'react';
/**
 * SentenceErrorJudgeRenderer — Pick which sentence contains a spelling error.
 *
 * The student sees 4 sentences and taps the one with the error.
 * Data format: data.sentences = [{ text, hasError, correction?, explanation? }]
 */
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
/** Convert hex color to rgba string */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
const FALLBACK_COLOR = '#6366F1'; // indigo-500
interface SentenceItem {
  text: string;
  hasError: boolean;
  correction?: string;
  explanation?: string;
}
export function SentenceErrorJudgeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const sentences: SentenceItem[] = config.sentences as SentenceItem[] || [];
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setSelectedIndex(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(selectedIndex !== null ? selectedIndex : -1);
    }
  }, [shouldValidate, selectedIndex, onAnswer]);
  const handleSelect = (index: number) => {
    if (shouldValidate) return;
    setSelectedIndex(index);
  };
  return <div className="space-y-3" data-id="element-8197">
      <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4" data-id="element-8198">
        <AlertCircleIcon className="w-4 h-4" data-id="element-8199" />
        Klik op de zin met de fout
      </p>
      {sentences.map((sentence, index) => {
      const isSelected = selectedIndex === index;
      return <motion.button key={index} type="button" onClick={() => handleSelect(index)} whileTap={{
        scale: 0.98
      }} className={`
              w-full text-left p-4 rounded-xl border-2 transition-all duration-150
              ${isSelected ? 'shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}
              ${shouldValidate ? 'pointer-events-none' : 'cursor-pointer'}
            `} style={isSelected ? {
        borderColor: color,
        backgroundColor: hexToRgba(color, 0.05),
        // @ts-ignore
        '--tw-ring-color': hexToRgba(color, 0.2),
        // @ts-ignore
        '--tw-ring-offset-width': '0px',
        // @ts-ignore
        '--tw-ring-offset-color': '#fff',
        // @ts-ignore
        '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
        // @ts-ignore
        '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
        boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
      } : undefined} onMouseEnter={e => {
        if (!isSelected && !shouldValidate) {
          e.currentTarget.style.borderColor = hexToRgba(color, 0.3);
        }
      }} onMouseLeave={e => {
        if (!isSelected && !shouldValidate) {
          e.currentTarget.style.borderColor = '';
        }
      }} data-id="element-8200">
            <div className="flex items-start gap-3" data-id="element-8201">
              <span className={`
                flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5
                ${isSelected ? 'text-white' : 'bg-gray-100 text-gray-500'}
              `} style={isSelected ? {
            backgroundColor: color
          } : undefined} data-id="element-8202">
                {index + 1}
              </span>
              <span className={`text-base leading-relaxed ${isSelected ? 'font-medium' : 'text-gray-800'}`} style={isSelected ? {
            color: color
          } : undefined} data-id="element-8203">
                {sentence.text}
              </span>
              {isSelected && <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5 ml-auto" style={{
            color: color
          }} data-id="element-8204" />}
            </div>
          </motion.button>;
    })}
    </div>;
}